import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfigService } from '../services/config.service';
import { ErrorHandlingService } from '../services/error-handling.service';

export interface ApiError {
  message: string;
  status: number;
}

@Injectable({
  providedIn: 'root',
})
export class ApiClient {
  private http = inject(HttpClient);
  private errorHandlingService = inject(ErrorHandlingService);
  private configService = inject(ConfigService);

  private get baseUrl(): string {
    return `${this.configService.getApiBaseUrl()}/api`;
  }

  get<T>(endpoint: string, params?: Record<string, string | number | boolean>): Observable<T> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }
    return this.http
      .get<T>(`${this.baseUrl}${endpoint}`, { params: httpParams })
      .pipe(catchError(this.handleError));
  }

  /**
   * Sends a POST request to the specified endpoint.
   *
   * @param endpoint - The API endpoint path (e.g., '/users')
   * @param body - The request body (typed as unknown to accept any serializable data)
   * @returns Observable that emits the response type T
   */
  post<T>(endpoint: string, body: unknown): Observable<T> {
    return this.http.post<T>(`${this.baseUrl}${endpoint}`, body).pipe(catchError(this.handleError));
  }

  /**
   * Sends a PUT request to the specified endpoint.
   *
   * @param endpoint - The API endpoint path (e.g., '/users/123')
   * @param body - The request body (typed as unknown to accept any serializable data)
   * @returns Observable that emits the response type T
   */
  put<T>(endpoint: string, body: unknown): Observable<T> {
    return this.http.put<T>(`${this.baseUrl}${endpoint}`, body).pipe(catchError(this.handleError));
  }

  /**
   * Sends a PATCH request to the specified endpoint.
   *
   * @param endpoint - The API endpoint path (e.g., '/users/123')
   * @param body - The request body (typed as unknown to accept any serializable data)
   * @returns Observable that emits the response type T
   */
  patch<T>(endpoint: string, body: unknown): Observable<T> {
    return this.http
      .patch<T>(`${this.baseUrl}${endpoint}`, body)
      .pipe(catchError(this.handleError));
  }

  delete<T>(endpoint: string): Observable<T> {
    return this.http.delete<T>(`${this.baseUrl}${endpoint}`).pipe(catchError(this.handleError));
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    const apiError: ApiError = {
      message: this.errorHandlingService.extractErrorMessage(error),
      status: error.status,
    };
    return throwError(() => apiError);
  };
}
